import React from "react";
import { Link } from "react-router-dom";
import { Eye, ArrowLeft, ShieldCheck, AlertTriangle } from "lucide-react";
import { useI18n, localeFor } from "@/i18n";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { DEFAULT_LANG, pathForLang, privacyPathForLang } from "@/lib/seo";
import { privacyDoc } from "@/lib/legal/index";
import {
  PRIVACY_EFFECTIVE_DATE,
  PRIVACY_VERSION,
  formatLegalDate,
  hasPlaceholders,
  placeholderFields,
} from "@/lib/legal";

// One block renderer per shape used by the content files. Anything unknown is dropped
// rather than rendered raw: a legal page must never show a stray object literal.
function Block({ block }) {
  if (block.p) return <p className="mt-4 leading-relaxed text-slate-600">{block.p}</p>;

  if (block.ul) {
    return (
      <ul className="mt-4 space-y-2">
        {block.ul.map((item, i) => (
          <li key={i} className="flex gap-3 leading-relaxed text-slate-600">
            <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary/60" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    );
  }

  if (block.note) {
    return (
      <div className="mt-5 flex gap-3 rounded-xl border border-primary/20 bg-primary/5 p-4">
        <ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" strokeWidth={2} />
        <p className="text-sm leading-relaxed text-slate-700">{block.note}</p>
      </div>
    );
  }

  if (block.table) {
    const { head, rows } = block.table;
    const labelled = head.some(Boolean);
    return (
      // The wrapper scrolls, not the page: four-column tables do not fit a phone, and a
      // body that scrolls sideways breaks every other section too.
      <div className="mt-5 -mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
        <table className="w-full min-w-[34rem] border-collapse text-left text-sm">
          {labelled && (
            <thead>
              <tr className="border-b border-slate-200">
                {head.map((h, i) => (
                  <th key={i} className="py-2 pr-4 align-bottom font-semibold text-slate-900">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className="border-b border-slate-100 last:border-0">
                {row.map((cell, j) => (
                  <td
                    key={j}
                    className={`py-3 pr-4 align-top leading-relaxed ${
                      !labelled && j === 0 ? "w-1/3 font-medium text-slate-900" : "text-slate-600"
                    }`}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return null;
}

export default function PrivacyPolicy({ lang }) {
  const { t, lang: current, setLang } = useI18n();

  // Same contract as <Landing>: the URL already picked the language (detectLang reads the
  // path first), so this only carries that choice into the app the visitor may enter next.
  // The root policy is skipped for the same reason the root landing is — it is the
  // x-default, and a bare visit must not overwrite a returning user's saved language.
  React.useEffect(() => {
    if (lang && lang !== DEFAULT_LANG && lang !== current) setLang(lang);
  }, [lang, current, setLang]);

  const pageLang = lang || current;
  const doc = privacyDoc(pageLang);
  const updated = formatLegalDate(PRIVACY_EFFECTIVE_DATE, localeFor(pageLang));

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900" data-testid="privacy-page">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-4xl items-center justify-between px-4 sm:px-6">
          <a href={pathForLang(pageLang)} className="flex items-center gap-2.5" data-testid="privacy-home-link">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-white">
              <Eye className="h-5 w-5" strokeWidth={2} />
            </span>
            <span className="leading-tight">
              <span className="block text-base font-bold tracking-tight">VisioScreening</span>
              <span className="block text-[11px] font-medium uppercase tracking-widest text-slate-400">
                {t("brand_sub")}
              </span>
            </span>
          </a>
          <LanguageSwitcher hrefFor={privacyPathForLang} />
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-10 sm:px-6 sm:py-14">
        <a
          href={pathForLang(pageLang)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 transition-colors hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4" /> {doc.backLabel}
        </a>

        <h1 className="mt-6 text-3xl font-extrabold tracking-tight sm:text-4xl">{doc.title}</h1>
        <p className="mt-4 max-w-3xl leading-relaxed text-slate-500">{doc.subtitle}</p>
        <p className="mt-4 text-sm text-slate-400">
          {doc.updatedLabel}: <time dateTime={PRIVACY_EFFECTIVE_DATE}>{updated}</time> · {doc.versionLabel}{" "}
          {PRIVACY_VERSION}
        </p>

        {doc.authoritativeNote && (
          <p className="mt-4 rounded-lg bg-slate-100 px-4 py-3 text-sm text-slate-500">{doc.authoritativeNote}</p>
        )}

        {/* Unfilled controller identity is a compliance defect, not a cosmetic one, so it is
            loud in development and invisible in the shipped bundle. */}
        {process.env.NODE_ENV !== "production" && hasPlaceholders() && (
          <div className="mt-6 flex gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0" />
            <span>
              <strong>Development warning:</strong> the data controller is not filled in yet. Set{" "}
              <code className="font-mono">{placeholderFields().join(", ")}</code> in{" "}
              <code className="font-mono">src/lib/legal.js</code> before going live — art. 13(1)(a) GDPR requires
              a named controller.
            </span>
          </div>
        )}

        <nav aria-label={doc.tocLabel} className="mt-10 rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-400">{doc.tocLabel}</h2>
          <ol className="mt-4 grid gap-x-8 gap-y-2 sm:grid-cols-2">
            {doc.sections.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`} className="text-sm text-slate-600 transition-colors hover:text-primary">
                  {s.h}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="mt-12 space-y-12">
          {doc.sections.map((s) => (
            <section key={s.id} id={s.id} className="scroll-mt-20">
              <h2 className="text-xl font-bold tracking-tight sm:text-2xl">{s.h}</h2>
              {s.blocks.map((block, i) => (
                <Block key={i} block={block} />
              ))}
            </section>
          ))}
        </div>
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto flex max-w-4xl flex-col items-center justify-between gap-3 px-4 py-8 text-sm text-slate-400 sm:flex-row sm:px-6">
          <span>
            {doc.updatedLabel}: {updated}
          </span>
          <Link to={pathForLang(pageLang)} className="transition-colors hover:text-slate-900">
            {doc.backLabel}
          </Link>
        </div>
      </footer>
    </div>
  );
}
